import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Palette, Image, Type, Sparkles, Loader2, Download, 
  RefreshCw, Wand2, Settings2, ImagePlus, Trash2, ImageIcon
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const projectTypes = [
  { id: 'image', label: 'Generate Image', icon: ImagePlus, description: 'Create AI images' },
  { id: 'poster', label: 'Poster Ideas', icon: Image, description: 'Design concepts' },
  { id: 'logo', label: 'Logo Ideas', icon: Sparkles, description: 'Brand identity' },
  { id: 'social', label: 'Social Media', icon: Type, description: 'Content ideas' },
];

export default function CreativeStudioPage() {
  const { token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [projectType, setProjectType] = useState('image');
  const [prompt, setPrompt] = useState('');
  const [theme, setTheme] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState('');
  
  // Image Generation State
  const [styles, setStyles] = useState([]);
  const [aspectRatios, setAspectRatios] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedRatio, setSelectedRatio] = useState('1:1');
  const [generatedImages, setGeneratedImages] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchImageStyles();
    
    // Check for passed prompt/style from Gallery
    if (location.state?.prompt) {
      setPrompt(location.state.prompt);
      if (location.state.style) {
        setSelectedStyle(location.state.style);
        setShowSettings(true);
      }
      toast.info('Prompt loaded from gallery');
    }
  }, [location.state]);

  const fetchImageStyles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/image/styles`);
      setStyles(response.data.styles || []);
      setAspectRatios(response.data.aspect_ratios || []);
    } catch (error) {
      console.error('Failed to fetch styles:', error);
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt to generate an image');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/image/generate`,
        {
          prompt: prompt.trim(),
          style: selectedStyle || null,
          aspect_ratio: selectedRatio || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success && response.data.images) {
        setGeneratedImages(prev => [...response.data.images, ...prev]);
        toast.success('Image generated successfully!');
      } else {
        toast.error(response.data.error || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const generateIdeas = async () => {
    if (!theme && !description) {
      toast.error('Please provide a theme or description');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/creative/generate-idea`,
        {
          type: projectType,
          theme,
          description
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setIdeas(response.data.ideas);
    } catch (error) {
      toast.error('Failed to generate ideas');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (imageData, index) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `aira-generated-${Date.now()}-${index}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image downloaded!');
  };

  const removeImage = (index) => {
    setGeneratedImages(prev => prev.filter((_, i) => i !== index));
  };

  const isImageMode = projectType === 'image';

  return (
    <div className="max-w-6xl mx-auto" data-testid="creative-studio-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">
          <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent">
            AIRA Creative Studio
          </span>
        </h1>
        <p className="text-[var(--text-secondary)]">AI-powered image generation and creative assistance</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <GlassCard className="p-6">
          <h3 className="font-heading text-xl font-semibold mb-6 text-white">Create Something Amazing</h3>

          {/* Project Type */}
          <div className="mb-6">
            <label className="block text-sm text-[var(--text-tertiary)] mb-3">Creation Mode</label>
            <div className="grid grid-cols-2 gap-3">
              {projectTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setProjectType(type.id)}
                  className={`flex flex-col items-start gap-1 p-4 rounded-xl transition-all ${
                    projectType === type.id
                      ? 'bg-[var(--color-primary)]/20 border border-[var(--color-primary)]/50'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                  data-testid={`project-type-${type.id}`}
                >
                  <div className="flex items-center gap-2">
                    <type.icon className={`w-5 h-5 ${projectType === type.id ? 'text-[var(--color-primary)]' : 'text-[var(--text-tertiary)]'}`} />
                    <span className={projectType === type.id ? 'text-white font-medium' : 'text-[var(--text-secondary)]'}>{type.label}</span>
                  </div>
                  <span className="text-xs text-[var(--text-tertiary)]">{type.description}</span>
                </button>
              ))}
            </div>
          </div>

          {isImageMode ? (
            <>
              {/* Image Generation Prompt */}
              <div className="mb-4">
                <label className="block text-sm text-[var(--text-tertiary)] mb-2">Image Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="input-field w-full h-28 resize-none"
                  placeholder="Describe the image you want to create... e.g., 'A futuristic cityscape at sunset with flying cars and neon lights'"
                  data-testid="image-prompt-input"
                />
              </div>

              {/* Settings Toggle */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-white mb-4 transition-colors"
              >
                <Settings2 className="w-4 h-4" />
                {showSettings ? 'Hide Settings' : 'Show Style Settings'}
              </button>

              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    {/* Style Selection */}
                    <div className="mb-4">
                      <label className="block text-sm text-[var(--text-tertiary)] mb-2">Style (Optional)</label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setSelectedStyle('')}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                            !selectedStyle
                              ? 'bg-[var(--color-primary)]/30 text-white border border-[var(--color-primary)]/50'
                              : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                          }`}
                        >
                          Auto
                        </button>
                        {styles.slice(0, 6).map((style) => (
                          <button
                            key={style.id}
                            onClick={() => setSelectedStyle(style.id)}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                              selectedStyle === style.id
                                ? 'bg-[var(--color-primary)]/30 text-white border border-[var(--color-primary)]/50'
                                : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                            }`}
                            title={style.description}
                          >
                            {style.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Aspect Ratio */}
                    <div className="mb-4">
                      <label className="block text-sm text-[var(--text-tertiary)] mb-2">Aspect Ratio</label>
                      <div className="flex gap-2">
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.id}
                            onClick={() => setSelectedRatio(ratio.id)}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                              selectedRatio === ratio.id
                                ? 'bg-[var(--color-secondary)]/30 text-white border border-[var(--color-secondary)]/50'
                                : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                            }`}
                            title={ratio.description}
                          >
                            {ratio.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={generateImage}
                disabled={loading || !prompt.trim()}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="generate-image-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Image...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5" />
                    Generate Image
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Theme */}
              <div className="mb-4">
                <label className="block text-sm text-[var(--text-tertiary)] mb-2">Theme / Style</label>
                <input
                  type="text"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="input-field w-full"
                  placeholder="e.g., minimalist, vibrant, corporate, playful"
                  data-testid="theme-input"
                />
              </div>

              {/* Description */}
              <div className="mb-6">
                <label className="block text-sm text-[var(--text-tertiary)] mb-2">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input-field w-full h-32 resize-none"
                  placeholder="Describe what you want to create..."
                  data-testid="description-input"
                />
              </div>

              <button
                onClick={generateIdeas}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="generate-ideas-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Ideas...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Creative Ideas
                  </>
                )}
              </button>
            </>
          )}
        </GlassCard>

        {/* Output Section */}
        <GlassCard className="p-6">
          <h3 className="font-heading text-xl font-semibold mb-6 text-white">
            {isImageMode ? 'Generated Images' : 'Creative Ideas'}
          </h3>
          
          {isImageMode ? (
            generatedImages.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 max-h-[500px] overflow-y-auto pr-2">
                {generatedImages.map((img, index) => (
                  <motion.div
                    key={img.id || index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative group"
                  >
                    <img
                      src={`data:${img.mime_type || 'image/png'};base64,${img.data}`}
                      alt={`Generated image ${index + 1}`}
                      className="w-full rounded-xl border border-white/10"
                      data-testid={`generated-image-${index}`}
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                      <button
                        onClick={() => downloadImage(img.data, index)}
                        className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                        title="Download"
                      >
                        <Download className="w-5 h-5 text-white" />
                      </button>
                      <button
                        onClick={() => removeImage(index)}
                        className="p-3 bg-white/10 rounded-full hover:bg-red-500/50 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center min-h-[300px]">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20 flex items-center justify-center mx-auto mb-4">
                    <ImagePlus className="w-8 h-8 text-[var(--color-primary)]" />
                  </div>
                  <p className="text-[var(--text-secondary)]">
                    Your AI-generated images will appear here
                  </p>
                  <p className="text-sm text-[var(--text-tertiary)] mt-2">
                    Powered by Nano Banana
                  </p>
                </div>
              </div>
            )
          ) : ideas ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="prose prose-invert max-w-none"
            >
              <div className="whitespace-pre-wrap text-[var(--text-secondary)] leading-relaxed">
                {ideas}
              </div>
            </motion.div>
          ) : (
            <div className="h-full flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20 flex items-center justify-center mx-auto mb-4">
                  <Palette className="w-8 h-8 text-[var(--color-primary)]" />
                </div>
                <p className="text-[var(--text-secondary)]">
                  Your AI-generated design ideas will appear here
                </p>
              </div>
            </div>
          )}
        </GlassCard>
      </div>

      {/* Feature Preview */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <GlassCard className="p-5 text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center mx-auto mb-3">
            <ImagePlus className="w-6 h-6 text-pink-400" />
          </div>
          <h4 className="font-semibold mb-1 text-white">AI Image Generation</h4>
          <p className="text-sm text-[var(--text-tertiary)]">Create stunning images from text</p>
        </GlassCard>
        
        <GlassCard className="p-5 text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-6 h-6 text-blue-400" />
          </div>
          <h4 className="font-semibold mb-1 text-white">Multiple Styles</h4>
          <p className="text-sm text-[var(--text-tertiary)]">Photorealistic, anime, digital art & more</p>
        </GlassCard>
        
        <GlassCard className="p-5 text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-yellow-500/20 flex items-center justify-center mx-auto mb-3">
            <Download className="w-6 h-6 text-orange-400" />
          </div>
          <h4 className="font-semibold mb-1 text-white">Instant Download</h4>
          <p className="text-sm text-[var(--text-tertiary)]">Save images directly to your device</p>
        </GlassCard>
        
        <GlassCard 
          className="p-5 text-center cursor-pointer hover:bg-white/5 transition-colors"
          onClick={() => navigate('/gallery')}
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 flex items-center justify-center mx-auto mb-3">
            <ImageIcon className="w-6 h-6 text-purple-400" />
          </div>
          <h4 className="font-semibold mb-1 text-white">My Creations</h4>
          <p className="text-sm text-[var(--text-tertiary)]">View your image gallery</p>
        </GlassCard>
      </div>
    </div>
  );
}
